import React, {useCallback, useState} from 'react';

import {useEventTracking} from 'src/hooks/useEventTracking';

import {FlatButton} from '@pb/kit-component-button';
import Group from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Loading from '@pb/kit-component-loading';
import {Message} from '@pb/kit-component-message';

import {useValidateCertificate} from '../../../hooks/use-validate-certificate';
import {loadingIndicator, successIndicator, tabContent} from '../upload-metadata.module.styl';
import {validateXmlFile} from '../utils/metadata-validation';
import {parseSamlMetadataXml} from '../utils/saml-metadata-parser';

import {ProviderInstructions} from './provider-instructions';
import {XmlDropZone} from './xml-drop-zone';

export type XmlTabData = {
    fileName: string | null,
    content: string | null,
    loginUrl: string,
    certificate: string,
    valid: boolean,
};

type MetadataXmlTabProps = {
    data: XmlTabData,
    onChange: (data: XmlTabData) => void,
    providerId: string | null,
};

export const MetadataXmlTab: React.FC<MetadataXmlTabProps> = ({data, onChange, providerId}) => {
    const [parseError, setParseError] = useState<string | null>(null);
    const {validate, loading: certLoading, error: certApiError, clearError: clearCertError} = useValidateCertificate();
    const {metadataValidate} = useEventTracking();

    const displayError = parseError ?? certApiError?.detailMessages?.[0] ?? null;

    const handleFileSelect = useCallback(async (file: File) => {
        const result = await validateXmlFile(file);
        if (!result.valid) {
            metadataValidate({method: 'file', valid: false});
            setParseError(result.error);
            clearCertError();
            onChange({fileName: file.name, content: null, loginUrl: '', certificate: '', valid: false});
            return;
        }

        const reader = new FileReader();
        reader.onload = async () => {
            const content = reader.result as string;
            const parsed = parseSamlMetadataXml(content);

            if (!parsed.loginUrl || !parsed.certificate) {
                metadataValidate({method: 'file', valid: false});
                setParseError(
                    'The metadata file does not contain the required IdP Login URL or signing certificate. '
                    + 'Please check the file or use the Manual Entry tab.',
                );
                onChange({fileName: file.name, content, loginUrl: '', certificate: '', valid: false});
                return;
            }

            setParseError(null);
            onChange({
                fileName: file.name,
                content,
                loginUrl: parsed.loginUrl,
                certificate: parsed.certificate,
                valid: false,
            });

            const certValid = await validate(parsed.certificate);
            metadataValidate({method: 'file', valid: certValid});
            onChange({
                fileName: file.name,
                content,
                loginUrl: parsed.loginUrl,
                certificate: parsed.certificate,
                valid: certValid,
            });
        };
        reader.onerror = () => {
            metadataValidate({method: 'file', valid: false});
            setParseError('Failed to read the file. Please try again.');
            onChange({fileName: file.name, content: null, loginUrl: '', certificate: '', valid: false});
        };
        reader.readAsText(file);
    }, [onChange, validate, clearCertError, metadataValidate]);

    const handleRemove = useCallback(() => {
        setParseError(null);
        clearCertError();
        onChange({fileName: null, content: null, loginUrl: '', certificate: '', valid: false});
    }, [onChange, clearCertError]);

    return (
        <div className={tabContent}>
            <Message color="info">
                You&apos;ll need the <strong>Federation Metadata XML</strong> file from your Identity Provider.
                Follow the steps below to locate and download it, then upload it here.
            </Message>

            <ProviderInstructions providerId={providerId} tabKey="file"/>

            {data.valid ? (
                <Group gap="xs" alignItems="center" className={successIndicator}>
                    <Icon type={Icons.CHECK} size="Size_14"/>
                    <span>IdP Login URL and signing certificate retrieved successfully</span>
                    <FlatButton
                        style={{marginLeft: 'auto'}}
                        onClick={handleRemove}>
                        Clear IdP Metadata
                    </FlatButton>
                </Group>
            ) : (
                <>
                    <XmlDropZone
                        onFileSelect={handleFileSelect}
                        onRemove={handleRemove}
                        error={displayError}
                        fileName={data.fileName}
                    />
                    {certLoading ? (
                        <div className={loadingIndicator}>
                            <Loading type="ICON"/>
                            <span>Validating certificate...</span>
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
};
